import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSignal } from "@preact/signals-react";
import PresentationModal from "../../../organisms/PresentationModal";
import { removeErrorField } from "../../../../utils/formErrorUtils";
import { ErrorField } from "../../../../types/ErrorField";
import Select, { ActionMeta, OnChangeValue, StylesConfig } from 'react-select';

import { FaArrowRight } from "react-icons/fa";
import NewTooltip from "../../../atoms/NewTooltip";
import { toast } from "react-toastify";
import { useState, useEffect } from "react";
import TextEdit from "../../../molecules/TextEdit";
import useFetch from "../../../../hooks/useFetch";
import { ProfileStorageManager } from "../../../../utils/profileStorageManager";

type Form = {
    profiles: string[];
}

type ProfileOption = {
    value: string;
    label: string;
    isFixed?: boolean;
}

const styles: StylesConfig<ProfileOption, true> = {
    multiValue: (base, state) => {
        return state.data.isFixed ? { ...base, backgroundColor: 'gray' } : base;
    },
    multiValueLabel: (base, state) => {
        return state.data.isFixed
            ? { ...base, fontWeight: 'bold', color: 'white', paddingRight: 6 }
            : base;
    },
    multiValueRemove: (base, state) => {
        return state.data.isFixed ? { ...base, display: 'none' } : base;
    },
    menu: (base) => ({
        ...base,
        zIndex: 9999,
    }),
    menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
    }),
};

const orderOptions = (values: readonly ProfileOption[]) => {
    return values
        .filter((v) => v.isFixed)
        .concat(values.filter((v) => !v.isFixed));
};

const SelectProfilesModal: React.FC = () => {
    const navigate = useNavigate();
    const errorFields = useSignal<ErrorField[]>([]);

    const [params] = useSearchParams();
    const profiles = params.get('profiles');
    const personType = params.get('personType') as 'individual' | 'legal_person';
    
    // ✅ Campos dinâmicos baseados no personType
    const documentValue = params.get(personType === 'individual' ? 'socialId' : 'govTaxId');
    const nameValue = params.get(personType === 'individual' ? 'name' : 'partnerName');
    
    const documentLabel = personType === 'individual' ? 'CPF' : 'CNPJ';
    const nameLabel = personType === 'individual' ? 'Nome' : 'Parceira';
    const editPath = personType === 'individual' ? '/person/individual/edit' : '/person/partners/edit';

    const { personId = "" } = useParams();
    console.log('🔍 Generic Modal personId:', personId, 'personType:', personType);

    // ✅ API endpoint dinâmico
    const PERSON_API_URL = process.env.REACT_APP_PERSON_API_URL || '';
    const API_KEY = process.env.REACT_APP_API_KEY || '';
    
    // ✅ API endpoint dinâmico (CORRIGIDO: legal_person → legal-person)
    const apiPersonType = personType === 'legal_person' ? 'legal-person' : personType;
    const { data: profilesData, isLoading: profilesLoading } = useFetch(
        `${PERSON_API_URL}/profiles/${apiPersonType}`,
        [{ key: 'x-api-key', value: API_KEY }]
    );
    
    const [availableProfiles, setAvailableProfiles] = useState<ProfileOption[]>([]);

    useEffect(() => {
        if (profilesData?.profiles) {
            const profileOptions = profilesData.profiles.map((profile: any) => ({
                value: profile.value,
                label: profile.label,
                isFixed: false,
            }));
            setAvailableProfiles(profileOptions);
        }
    }, [profilesData]);

    const initialProfiles = profiles ? profiles?.split(',') : [];

    const form = useSignal<Form>({
        profiles: initialProfiles,
    });

    const [selectedOptions, setSelectedOptions] = useState<readonly ProfileOption[]>([]);
    const [fixedProfiles, setFixedProfiles] = useState<readonly ProfileOption[]>([]);

    useEffect(() => {
        if (availableProfiles.length > 0) {
            const fixed = initialProfiles.map(profile => ({
                value: profile,
                label: availableProfiles.find(p => p.value === profile)?.label || profile,
                isFixed: true,
            }));
            setFixedProfiles(fixed);
            setSelectedOptions(orderOptions(fixed));
        }
    }, [availableProfiles]);

    const changeForm = (field: string, value: any) => {
        form.value = { ...form.value, [field]: value };
        removeErrorField(errorFields.value, field);
    };

    const submitForm = async () => {
        if (form.value.profiles.length === 0) {
            toast.error("Selecione ao menos um perfil!");
            return;
        }
        if (form.value.profiles.length > 0 && form.value.profiles.length === initialProfiles.length) {
            toast.error("Selecione um perfil diferente do perfil atual!");
            return;
        }
        
        // ✅ ProfileStorageManager dinâmico
        ProfileStorageManager.save(personType, personId, form.value.profiles);
        console.log('💾 Profiles salvos em localStorage:', form.value.profiles);
        
        // ✅ Navegação dinâmica
        navigate(`${editPath}/${personId}`);
    };

    const onChange = (
        newValue: OnChangeValue<ProfileOption, true>,
        actionMeta: ActionMeta<ProfileOption>,
    ) => {
        switch (actionMeta.action) {
            case 'remove-value':
            case 'pop-value':
                if (actionMeta.removedValue.isFixed) {
                    return;
                }
                break;
            case 'clear':
                newValue = fixedProfiles;
                break;
        }

        const orderedValues = orderOptions(newValue);
        setSelectedOptions(orderedValues);
        changeForm('profiles', orderedValues.map(v => v.value));
    };

    const disableForward = () => {
        return selectedOptions.length === 0 || selectedOptions.length === fixedProfiles.length;
    };

    return (
        <PresentationModal
            id="select-profiles-modal"
            show={true}
            onClose={() => navigate(-1)}
            header="Adicionar novo perfil"
        >
            <div className="p-4">
                <div className="mb-4 h-64">
                    <div className="grid grid-cols-4 gap-4 mb-4">
                        <TextEdit 
                            disabled={true}
                            id="govTaxId"
                            label={documentLabel}
                            className="col-span-1"
                            value={documentValue || ""}
                        />
                        <TextEdit 
                            disabled={true} 
                            id="partner"
                            label={nameLabel}
                            className="col-span-3"
                            value={nameValue || ""}
                        />
                    </div>

                    <label htmlFor="profiles">Perfis:</label>
                    <Select
                        id="select-profiles"
                        inputId="profiles"
                        name="profiles"
                        isMulti={true}
                        closeMenuOnSelect={false}
                        isClearable={selectedOptions.some((v) => !v.isFixed)}
                        options={availableProfiles}
                        className="basic-multi-select"
                        classNamePrefix="select"
                        isLoading={profilesLoading}
                        onChange={onChange}
                        placeholder={profilesLoading ? "Carregando perfis..." : "Selecione o(s) perfil(is) desejado(s)"}
                        value={selectedOptions}
                        styles={styles}
                        menuPortalTarget={document.body}
                    />
                </div>
                <div className="flex justify-end">
                    <NewTooltip content={<div className="w-48">Cancelar a edição e voltar para a tela anterior</div>}>
                        <button
                            id="btn-cancel-profiles"
                            type="button"
                            onClick={() => navigate(-1)}
                            className="mr-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                        >
                            Cancelar
                        </button>
                    </NewTooltip>

                    <NewTooltip content={<div className="w-24">Avançar para a próxima etapa</div>}>
                        <button
                            id="btn-advance-profiles"
                            type="button"
                            onClick={submitForm}
                            disabled={disableForward()}
                            className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                        >
                            Avançar
                            <FaArrowRight className="ml-2" />
                        </button>
                    </NewTooltip>
                </div>
            </div>
        </PresentationModal>
    );
};

export default SelectProfilesModal;
